import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/forms/PasswordInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usersApi } from '@/lib/api/users';
import { passwordMinLength } from '@/config/passwordMinLength';
import { RoleOptions, type Role, type CreateUserRequest } from '@paladin-monorepo/common';

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateUserDialog({ open, onOpenChange }: CreateUserDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<Role>(RoleOptions.EMPLOYEE);

  const schema = yup.object({
    email: yup
      .string()
      .email(t('users.create.form.email.errors.invalid'))
      .required(t('users.create.form.email.errors.required')),
    password: yup
      .string()
      .min(passwordMinLength, t('users.create.form.password.errors.minLength', { minLength: passwordMinLength }))
      .required(t('users.create.form.password.errors.required')),
    firstName: yup
      .string()
      .required(t('users.create.form.firstName.errors.required')),
    lastName: yup
      .string()
      .required(t('users.create.form.lastName.errors.required')),
  }).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<CreateUserRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('users.create.success'));
      reset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      if (error.response?.data?.code === 'EmailAlreadyExists') {
        setError('email', { message: t('users.create.form.email.errors.exists') });
      } else {
        toast.error(t('users.create.error'));
      }
    },
  });

  const onSubmit = (data: CreateUserRequest) => {
    createMutation.mutate({
      ...data,
      role: selectedRole,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
      setSelectedRole(RoleOptions.EMPLOYEE);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{t('users.create.title')}</DialogTitle>
            <DialogDescription>
              {t('users.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('users.create.form.email.label')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('users.create.form.email.placeholder')}
                {...register('email')}
                disabled={createMutation.isPending}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('users.create.form.password.label')}</Label>
              <PasswordInput
                id="password"
                placeholder={t('users.create.form.password.placeholder')}
                {...register('password')}
                disabled={createMutation.isPending}
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('users.create.form.firstName.label')}</Label>
                <Input
                  id="firstName"
                  placeholder={t('users.create.form.firstName.placeholder')}
                  {...register('firstName')}
                  disabled={createMutation.isPending}
                />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{errors.firstName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('users.create.form.lastName.label')}</Label>
                <Input
                  id="lastName"
                  placeholder={t('users.create.form.lastName.placeholder')}
                  {...register('lastName')}
                  disabled={createMutation.isPending}
                />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{errors.lastName.message}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('users.create.form.role.label')}</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as Role)}
                disabled={createMutation.isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('users.create.form.role.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={RoleOptions.ACCOUNT_ADMIN}>
                    {t('codebook.role.ACCOUNT_ADMIN')}
                  </SelectItem>
                  <SelectItem value={RoleOptions.EMPLOYEE}>
                    {t('codebook.role.EMPLOYEE')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t('users.create.form.role.hint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
            >
              {t('generic.cancel')}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? t('generic.loading') : t('users.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
